import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  isDevMode,
  OnInit,
  Renderer2,
  TemplateRef,
  untracked,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Auth } from '@angular/fire/auth';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogContainer,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { ChatFocusOverlay } from '@components/comments/chat-focus-overlay.component';
import {
  PuebloConfetti,
  PuebloConfettiComponent,
} from '@components/confetti/confetti.component';
import { PuebloDialogsComponent } from '@components/dialogs/pueblo-dialogs.component';
import { PuebloMenusComponent } from '@components/dialogs/pueblo-menus.component';
import { PuebloSheetsComponent } from '@components/dialogs/pueblo-sheets.component';
import { HangDrawer } from '@components/forms/hang/drawer/hang-drawer.component';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { DiscoverPage } from '@pages/discover/discover.page';
import { HomePage } from '@pages/hangs/hangs.page';
import { ProfilePage } from '@pages/profile/profile.page';
import { PuebloRouter } from '@routing/pueblo-router.service';
import { AppUserAuthService } from '@services/app-user.service';
import { PageCache } from '@services/page-cache.service';
import { MobileDevice, PlatformService } from '@services/platform.service';
import { compareVersions } from '@utils/version-utils';
import { MIN_ANDROID_VERSION, MIN_IOS_VERSION } from '../../capacitor.config';
import { Login } from '../components/login/login.component';
import { ScrollInputComponent } from '../components/scroll-input/scroll-input.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './app.component.scss',
  imports: [
    ChatFocusOverlay,
    HangDrawer,
    IonApp,
    IonRouterOutlet,
    Login,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    NgClass,
    PuebloConfettiComponent,
    PuebloDialogsComponent,
    PuebloMenusComponent,
    PuebloSheetsComponent,
    ScrollInputComponent,
  ],
})
export class AppComponent implements OnInit {
  private readonly auth = inject(Auth);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly pageCache = inject(PageCache);
  private readonly platformService = inject(PlatformService);
  private readonly puebloConfetti = inject(PuebloConfetti);
  private readonly puebloRouter = inject(PuebloRouter);
  private readonly route = inject(ActivatedRoute);
  private readonly renderer = inject(Renderer2);

  protected readonly appUserAuthService = inject(AppUserAuthService); // Do not remove

  protected readonly reloadAppDialog =
    viewChild.required<TemplateRef<MatDialogContainer>>('reloadAppDialog');
  protected readonly updateAppInStoreDialog = viewChild.required<
    TemplateRef<MatDialogContainer>
  >('updateAppInStoreDialog');

  private readonly $stowedPagesContainer = viewChild.required<
    ElementRef<HTMLElement>,
    ViewContainerRef
  >('stowedPagesContainer', { read: ViewContainerRef });

  private readonly $confetti =
    viewChild.required<PuebloConfettiComponent>('confetti');

  private latestBundleId = '';

  protected readonly appClasses = this.getPlatformClassString();

  protected readonly showTryAppBanner =
    this.platformService.isMobile() && !this.platformService.isApp();

  protected readonly storeLink = this.platformService.isApple()
    ? 'https://testflight.apple.com/join/vPgjVtAs'
    : 'https://play.google.com/store/apps/details?id=app.pueblo.app';

  constructor() {
    let authStateInit = false;
    effect(() => {
      if (authStateInit) return;
      authStateInit = true;
      const authStateReady = this.appUserAuthService.$authStateReady();
      if (!authStateReady) return;
      untracked(() => {
        setTimeout(() => {
          this.pageCache.preload([
            { key: 'hangs', componentType: HomePage },
            { key: 'discover', componentType: DiscoverPage },
            { key: 'profile', componentType: ProfilePage },
          ]);
        }, 1000);
      });
    });

    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (params) => {
        if (isDevMode() && params.anon !== undefined) {
          if (params.anon === 'true') {
            localStorage.setItem('anon', 'true');
            await this.auth.signOut();
            this.puebloRouter.clearParams(['anon']);
            setTimeout(() => {
              location.reload();
            }, 1000);
          } else if (params.anon === 'false') {
            this.puebloRouter.clearParams(['anon']);
            localStorage.removeItem('anon');
            setTimeout(() => {
              location.reload();
            }, 1000);
          }
        }
      });

    this.matIconRegistry.addSvgIcon(
      'pb_pueblo',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../assets/symbols/pueblo.svg',
      ),
    );
    this.matIconRegistry.addSvgIcon(
      'pb_hang',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../assets/symbols/hang-2.svg',
      ),
    );
    this.matIconRegistry.addSvgIcon(
      'pb_link_qr',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../assets/symbols/link-qr.svg',
      ),
    );
    this.matIconRegistry.addSvgIcon(
      'pb_subscribe_pueblo',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../assets/symbols/subscribe-pueblo-v1.svg',
      ),
    );
    this.matIconRegistry.addSvgIcon(
      'pb_leave_pueblo',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../assets/symbols/leave-pueblo-v1.svg',
      ),
    );
    this.matIconRegistry.addSvgIcon(
      'pb_subscribed_pueblo',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '../assets/symbols/subscribed-pueblo-v1.svg',
      ),
    );
    this.matIconRegistry.setDefaultFontSetClass('material-symbols-outlined');
  }

  async ngOnInit(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await CapacitorUpdater.notifyAppReady();

      await CapacitorUpdater.addListener('updateAvailable', async (event) => {
        this.latestBundleId = event.bundle.id;

        const currentAppVersion = (await App.getInfo()).version;

        let minAppVersion = '';
        if (this.platformService.isIosApp()) {
          minAppVersion = MIN_IOS_VERSION;
        } else if (this.platformService.isAndroidApp()) {
          minAppVersion = MIN_ANDROID_VERSION;
        }

        if (compareVersions(currentAppVersion, minAppVersion) < 0) {
          this.openUpdateAppInStoreDialog();
        } else {
          this.openReloadAppDialog();
        }
      });

      await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        this.deepLink(event.url);
      });

      await FirebaseMessaging.addListener(
        'notificationActionPerformed',
        (event) => {
          if (event.actionId === 'tap') {
            const url = (event.notification.data as { link: string }).link;
            this.deepLink(url);
          }
        },
      );

      Keyboard.addListener('keyboardWillShow', (event) => {
        this.renderer.addClass(document.documentElement, 'keyboard-open');
      });
      Keyboard.addListener('keyboardWillHide', () => {
        this.renderer.removeClass(document.documentElement, 'keyboard-open');
      });
    }

    this.pageCache.registerStowedPagesContainer(this.$stowedPagesContainer());

    this.puebloConfetti.register(this.$confetti());

    // Keep this debug line since it's generally useful for debugging.
    console.log(this.platformService.debugString());
  }

  protected async reloadApp(): Promise<void> {
    await CapacitorUpdater.set({ id: this.latestBundleId });
  }

  private openUpdateAppInStoreDialog(): void {
    const updateAppInStoreDialog = this.updateAppInStoreDialog();
    if (!updateAppInStoreDialog) return;
    this.dialog.open(updateAppInStoreDialog);
  }

  private openReloadAppDialog(): void {
    const reloadAppDialog = this.reloadAppDialog();
    if (!reloadAppDialog) return;
    this.dialog.open(reloadAppDialog);
  }

  protected getPlatformClassString(): string {
    const platform = this.platformService.isApp()
      ? 'platform-app'
      : 'platform-web';
    const isMobile = this.platformService.isMobile()
      ? 'platform-mobile'
      : 'platform-desktop';
    const appPlatform = this.platformService.isApp()
      ? this.platformService.isIosApp()
        ? 'platform-ios'
        : 'platform-android'
      : '';
    const isPwa = this.platformService.isPwa() ? 'platform-pwa' : '';
    const mobileDevice =
      this.platformService.mobileDevice === MobileDevice.UNKNOWN ||
      this.platformService.mobileDevice === MobileDevice.NOT_MOBILE
        ? ''
        : `platform-device-${this.platformService.mobileDevice}`;
    const browser = `platform-browser-${this.platformService.browser}`;
    return `${platform} ${browser} ${isMobile} ${appPlatform} ${isPwa} ${mobileDevice}`;
  }

  private deepLink(url: string): void {
    setTimeout(() => {
      const slug = url.split('.app').pop();
      if (slug) {
        this.puebloRouter.goTo([slug], undefined, true);
      }
    }, 300);
  }
}
